import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/client';
import HelpButton from '../../help/HelpButton';

export default function FailoverPage() {
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: ['admin','failover','state'],
    queryFn:  () => adminApi.failoverState(),
    refetchInterval: 15_000,
  });
  const history = useQuery({
    queryKey: ['admin','failover','history'],
    queryFn:  () => adminApi.failoverHistory({ limit: 50 }),
  });
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const mut = useMutation({
    mutationFn: (p) => adminApi.doFailover(p),
    onSuccess: (r) => {
      setMsg({ ok: true, text: `Flipped ${r.from_cluster || '(none)'} → ${r.to_cluster} at ${new Date(r.at).toLocaleString()}` });
      setTarget(''); setReason(''); setConfirm(false);
      qc.invalidateQueries({ queryKey: ['admin','failover'] });
    },
    onError: (e) => setMsg({ ok: false, text: e?.detail || e?.message || 'Failed' }),
  });

  const clusters = state.data?.clusters || [];
  const active = state.data?.active;

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Cluster Failover</h2>
        <HelpButton moduleKey="admin_failover" />
      </div>

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel mb-4">
        <div className="text-xs uppercase text-surface-muted tracking-wider mb-2">Current state</div>
        <div className="flex gap-3 flex-wrap">
          {clusters.map(c => (
            <div key={c.name} className={'px-3 py-2 rounded-thin border ' +
              (c.state === 'active' ? 'border-accent-green/60 bg-accent-green/10' :
               c.state === 'disabled' ? 'border-accent-red/60 opacity-60' :
               'border-surface-border')}>
              <div className="font-semibold">{c.display_name || c.name.toUpperCase()}</div>
              <div className="text-xs text-surface-muted">{c.host_ip}</div>
              <div className={'text-xs mt-1 font-mono ' + (c.state === 'active' ? 'text-accent-green' : '')}>{c.state}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border p-4 bg-surface-panel dark:bg-surface-dark-panel space-y-3">
        <h3 className="text-sm font-semibold">Flip active cluster</h3>
        <div className="text-xs text-surface-muted">
          This only changes the ContinuumCX active-flag. It does NOT promote Postgres or reconfigure Kamailio.
          See FAILOVER-AND-DR.md for the full runbook.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-surface-muted mb-1">Target cluster</label>
            <select value={target} onChange={(e) => setTarget(e.target.value)}
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full">
              <option value="">—</option>
              {clusters.filter(c => c.state !== 'disabled').map(c => (
                <option key={c.name} value={c.name}>
                  {c.name} · {c.display_name} {c.name === active ? '(currently active)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-surface-muted mb-1">Reason</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="planned maintenance / emergency / testing / …"
              className="px-2 py-1 text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-base dark:bg-surface-dark-base w-full" />
          </div>
        </div>

        <label className="text-sm inline-flex items-center gap-2">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
          I understand this flips the active cluster and is audit-logged.
        </label>

        <div className="flex gap-2">
          <button
            onClick={() => mut.mutate({ to_cluster: target, reason: reason || null })}
            disabled={!confirm || !target || mut.isPending}
            className="px-3 py-1.5 text-sm rounded-thin bg-accent-red text-white hover:bg-red-700 disabled:opacity-40">
            {mut.isPending ? '…' : 'Flip'}
          </button>
        </div>

        {msg && (
          <div className={'text-sm p-2 rounded-thin ' + (msg.ok ? 'bg-accent-green/10 text-green-800 dark:text-green-300' : 'bg-accent-red/10 text-red-700 dark:text-red-300')}>
            {msg.text}
          </div>
        )}
      </div>

      <h3 className="text-sm font-semibold mt-6 mb-2">Recent history</h3>
      {(history.data?.events || []).length === 0 ? (
        <div className="text-sm text-surface-muted">No flips recorded yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
            <tr>
              <th className="px-2 py-1">When</th>
              <th className="px-2 py-1">From</th>
              <th className="px-2 py-1">To</th>
              <th className="px-2 py-1">Actor</th>
              <th className="px-2 py-1">Kind</th>
              <th className="px-2 py-1">Reason</th>
            </tr>
          </thead>
          <tbody>
            {(history.data.events || []).map(e => (
              <tr key={e.id} className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-2 py-1.5 text-xs">{new Date(e.at).toLocaleString()}</td>
                <td className="px-2 py-1.5 font-mono">{e.from_cluster || '—'}</td>
                <td className="px-2 py-1.5 font-mono">{e.to_cluster}</td>
                <td className="px-2 py-1.5">{e.actor_email || '—'}</td>
                <td className="px-2 py-1.5">{e.kind}</td>
                <td className="px-2 py-1.5 text-xs">{e.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
